import { Descriptions } from "antd";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { getSemesterTest, getSemesterTestPapers, openSemesterTestResult } from "../../../api/semesterTest.api";
import { remarkTestScores } from "../../../api/tutor.api";
import useAxios from "../../../hooks/useAxios";
import useNotification from "../../../hooks/useNotification";
import { getCommonCodeLabel } from "../../../lib/store";
import { IGetSemesterTest, IGetSemesterTestPapers } from "../../../models/semesterTest.model";
import { SemesterContents } from "../../../pages/program/semester";
import Button from "../../Button";
import ErrorResponse from "../../ErrorResponse";
import Loader from "../../Loader";
import ConfirmModal from "../../modals/ConfirmModal";
import EditLevelModal from "./modals/EditLevelModal";
import SelectPaperModal from "./modals/SelectPaperModal";

interface IProps {
  scheduleCode: number|undefined;
  setTab: (tab: SemesterContents) => void;
  setGetTestsTrigger: any;
}

const TestRead: FC<IProps> = ({
  scheduleCode,
  setTab,
  setGetTestsTrigger,
}) => {
  const { t, i18n } = useTranslation();
  const { successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetSemesterTest>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetSemesterTestPapers>();
  const { data: updateRes, isLoading: updateIsLoading, error: updateError, execute: updateExecute } = useAxios();
  const { data: updateRes2, isLoading: updateIsLoading2, error: updateError2, execute: updateExecute2 } = useAxios();

  const [confirmOpenVisible, setConfirmOpenVisible] = useState(false);
  const [selectPaperVisible, setSelectPaperVisible] = useState(false);
  const [editLevelVisible, setEditLevelVisible] = useState(false);
  const [examCode, setExamCode] = useState<number>();
  const [courseCodes, setCourseCodes] = useState<number[]>(); 
  const [confirmRemarkVisible, setConfirmRemarkVisible] = useState(false);
  const [examName, setExamName] = useState('');

  useEffect(() => {
    if (scheduleCode) {
      execute(getSemesterTest(scheduleCode));
      doGetSemesterTestPapers();
    }
  }, [scheduleCode]);

  useEffect(() => {
    if (updateRes) {
      setGetTestsTrigger((current: boolean) => !current);
      successNotification(t('program.semester.message25'));
      setTab(SemesterContents.Test);
    }
  }, [updateRes]);

  useEffect(() => {
    if (updateRes2) {
      successNotification(t('program.semester.message26'));
    }
  }, [updateRes2]);

  const doGetSemesterTestPapers = () => {
    if (!scheduleCode) return;
    execute2(getSemesterTestPapers(scheduleCode));
  }

  const onOpen = () => {
    if (!scheduleCode) return;
    updateExecute(openSemesterTestResult(scheduleCode));
  }

  const onRemarkTestScores = () => {
    setConfirmRemarkVisible(false);
    if (scheduleCode) {
      updateExecute2(remarkTestScores(scheduleCode));
    }
  }

  return (
    <>
      {(isLoading || isLoading2 || updateIsLoading || updateIsLoading2) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={updateError} />
      <ErrorResponse error={updateError2} />

      <div className='comm-table__area rowType edit'>
        <Descriptions
          bordered
          column={2}
          size="small"
          labelStyle={{ width: '15%' }}
          contentStyle={{ width: '35%' }}
        >
          <Descriptions.Item label={t('program.semester.column25')} span={2}>
            {res?.data?.name}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.label.1')} span={2}>
            {getCommonCodeLabel('PR0006', res?.data?.eduStepCode || '', i18n.language)}
          </Descriptions.Item>
          <Descriptions.Item label={t('program.semester.column24')}>
            {getCommonCodeLabel('TE0001', res?.data?.examDiv || '', 'ko')}
          </Descriptions.Item>
          <Descriptions.Item label={t('program.semester.column28')}>
            {res?.data?.checkYn === 'Y' ? t('program.semester.option4') : t('program.semester.option3')}
          </Descriptions.Item>
          <Descriptions.Item label={t('program.semester.column12')}>
            {moment(res?.data?.srtDate).format('YYYY.MM.DD')}
          </Descriptions.Item>
          <Descriptions.Item label={t('program.semester.column13')}>
          {moment(res?.data?.endDate).format('YYYY.MM.DD')}
          </Descriptions.Item>
          <Descriptions.Item label={t('program.semester.column9')}>
            {res?.data?.yearCode}
          </Descriptions.Item>
          <Descriptions.Item label={t('program.semester.column22')}>
            {res?.data?.testSeq}
          </Descriptions.Item>
          <Descriptions.Item label={t('CAC Date')}>
            {res?.data?.cacExamDate ? moment(res?.data?.cacExamDate).format('YYYY.MM.DD') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('program.semester.column42')}>
            {res?.data?.openYn ? res?.data?.openYn : '-'}
          </Descriptions.Item>
        </Descriptions>
      </div>

      <div className="comm-bx__between">
        <div className="comm-btn__area">
          <Button
            onClick={() => setTab(SemesterContents.Test)}
            className="btn-basic gray-line"
            text={t('common.button.list')}
          />
        </div>
        <div className="comm-btn__area right">
          <Button
            onClick={() => setTab(SemesterContents.TestEdit)}
            className="btn-basic pink-line"
            text={t('common.button.edit')}
          />
        </div>
      </div>

      <div>
        <div className="comm-bx__between">
          <div className="comm-sch_result">
            <h3 className='sub-title'>{t('program.semester.title5')}</h3>
          </div>
          <div className="comm-btn__area right">
            <Button
              onClick={() => setSelectPaperVisible(true)}
              className="btn-basic pink-line"
              text={t('common.button.add')}
            />
          </div>
        </div>
        <div className='comm-table__area rowType edit'>
          <table>
            <colgroup>
              <col width="20%" />
              <col width="20%" />
              <col width="*" />
              <col width="15%" />
            </colgroup>
            <tbody>
              <tr>
                <th className="text-center">{t('program.semester.column24')}</th>
                <th className="text-center">{t('program.semester.column25')}</th>
                <th className="text-center">{t('program.semester.column30')}</th>
                <th className="text-center">{t('program.semester.column31')}</th>
              </tr>
              {
                res2?.data?.length
                  ? res2.data.map(item => (
                    <tr>
                      <td className="text-center">
                        {getCommonCodeLabel('TE0001', item.examDiv, 'ko')}
                      </td>
                      <td className="text-center">
                        {item.examName}
                      </td>
                      <td>
                        <div className="flex flex-justify-between comm-btn__area">
                          {item.courseInfo?.map(course => course.courseName)?.join(', ')}
                          <Button
                            className="btn-basic small mint"
                            onClick={() => {
                              setExamCode(item.examCode);
                              setCourseCodes(item.courseInfo?.map(course => course.courseCode));
                              setEditLevelVisible(true);
                            }}
                            text={t('program.semester.button7')}
                          />
                        </div>
                      </td>
                      <td>
                        <div className="comm-btn__area justify-content-center">
                          <Button
                            className="btn-basic small mint"
                            onClick={() => {
                              setExamName(item.examName);
                              setConfirmRemarkVisible(true);
                            }}
                            text={t('program.semester.column31')}
                          />
                        </div>
                      </td>
                    </tr>
                    ))
                  : (
                    <tr>
                      <td className="text-center" colSpan={4}>
                        {t('program.semester.text7')}
                      </td>
                    </tr>
                    )
              }
            </tbody>
          </table>
        </div>
      </div>

      {confirmOpenVisible &&
        <ConfirmModal
          visible={confirmOpenVisible}
          setVisible={setConfirmOpenVisible}
          onConfirm={onOpen}
          titleText={t('program.semester.title6')}
          messageText={t('program.semester.text8')}
          confirmText={t('program.semester.button8')}
          cancelText={t('common.button.cancel')}
        />      
      }
      {selectPaperVisible &&
        <SelectPaperModal
          visible={selectPaperVisible}
          setVisible={setSelectPaperVisible}
          scheduleCode={scheduleCode}
          doGetSemesterTestPapers={doGetSemesterTestPapers}
          eduStepCode={res?.data?.eduStepCode}
        />
      }
      {editLevelVisible &&
        <EditLevelModal
          visible={editLevelVisible}
          setVisible={setEditLevelVisible}
          scheduleCode={scheduleCode}
          examCode={examCode}
          doGetSemesterTestPapers={doGetSemesterTestPapers}
          courseCodes={courseCodes}
        />
      }
      {confirmRemarkVisible &&
        <ConfirmModal
          visible={confirmRemarkVisible}
          setVisible={setConfirmRemarkVisible}
          onConfirm={onRemarkTestScores}
          titleText={t('program.semester.12')}
          messageText={`[${examName}]${t('program.semester.13')}`}
          confirmText={t('common.option.yes')}
          cancelText={t('common.option.no')}
        />      
      }
    </>
  );
}

export default TestRead;
